import React, { useState } from 'react';
import apiClient from '../shared/services/apiClient';
import { formatApiError } from '../utils/errorMessage';

/**
 * Invoice and quote details for one DBA / division: the address, contact,
 * number prefix and logo shown when a document is issued under this DBA.
 * Blank fields fall back to the business's own branding.
 */
export default function DivisionBillingSettings({ businessId, registration, onSaved }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    billing_address: registration.billing_address || '',
    contact_email: registration.contact_email || '',
    contact_phone: registration.contact_phone || '',
    number_prefix: registration.number_prefix || '',
  });
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const base = `/businesses/${businessId}/branding/divisions/${registration.id}`;

  const save = async (e) => {
    e.preventDefault();
    setSaving(true); setError(''); setMessage('');
    try {
      const res = await apiClient.put(base, form);
      setForm((prev) => ({ ...prev, number_prefix: res.data?.number_prefix || '' }));
      setMessage('Saved');
      if (onSaved) onSaved(res.data);
    } catch (err) {
      setError(formatApiError(err, 'Could not save'));
    } finally {
      setSaving(false);
    }
  };

  const uploadLogo = async (file) => {
    if (!file) return;
    setSaving(true); setError(''); setMessage('');
    try {
      const data = new FormData();
      data.append('logo', file);
      const res = await apiClient.post(`${base}/logo`, data, { headers: { 'Content-Type': 'multipart/form-data' } });
      setMessage('Logo saved');
      if (onSaved) onSaved(res.data);
    } catch (err) {
      setError(formatApiError(err, 'Could not upload the logo'));
    } finally {
      setSaving(false);
    }
  };

  if (!open) {
    return (
      <button type="button" onClick={() => setOpen(true)} className="mt-2 block text-xs font-medium text-blue-700 hover:text-blue-900 underline">
        Invoice &amp; quote details
      </button>
    );
  }

  const id = (name) => `div-${registration.id}-${name}`;
  return (
    <form onSubmit={save} className="mt-3 space-y-2 text-sm" aria-label="Invoice and quote details">
      <div>
        <label htmlFor={id('prefix')} className="block text-xs text-gray-600">Number prefix (e.g. ABC)</label>
        <input id={id('prefix')} value={form.number_prefix} maxLength={12}
          onChange={(e) => setForm({ ...form, number_prefix: e.target.value })}
          className="w-full border border-gray-300 rounded px-2 py-1" />
      </div>
      <div>
        <label htmlFor={id('address')} className="block text-xs text-gray-600">Address on invoices</label>
        <textarea id={id('address')} rows={2} value={form.billing_address}
          onChange={(e) => setForm({ ...form, billing_address: e.target.value })}
          className="w-full border border-gray-300 rounded px-2 py-1" />
      </div>
      <div>
        <label htmlFor={id('email')} className="block text-xs text-gray-600">Email</label>
        <input id={id('email')} type="email" value={form.contact_email}
          onChange={(e) => setForm({ ...form, contact_email: e.target.value })}
          className="w-full border border-gray-300 rounded px-2 py-1" />
      </div>
      <div>
        <label htmlFor={id('phone')} className="block text-xs text-gray-600">Phone</label>
        <input id={id('phone')} value={form.contact_phone}
          onChange={(e) => setForm({ ...form, contact_phone: e.target.value })}
          className="w-full border border-gray-300 rounded px-2 py-1" />
      </div>
      <div>
        <label htmlFor={id('logo')} className="block text-xs text-gray-600">Logo (PNG, JPG or WebP)</label>
        <input id={id('logo')} type="file" accept="image/png,image/jpeg,image/webp"
          onChange={(e) => uploadLogo(e.target.files && e.target.files[0])} className="text-xs" />
      </div>
      <p className="text-xs text-gray-500">Blank fields use the business&apos;s own details.</p>
      <div className="flex items-center gap-3">
        <button type="submit" disabled={saving} className="px-3 py-1 bg-blue-600 text-white rounded text-xs disabled:opacity-50">Save</button>
        <button type="button" onClick={() => setOpen(false)} className="text-xs text-gray-600 underline">Close</button>
        {message && <span className="text-xs text-green-700">{message}</span>}
      </div>
      {error && <div role="alert" className="text-xs text-red-700">{error}</div>}
    </form>
  );
}
